import React, { useEffect, useState } from "react";
import type { FormProps } from "antd";
import { Button, Form, Input } from "antd";
import { AxiosError } from "axios";
import { useAuth } from "src/hooks/auth.hook";
import { Link, useNavigate, useParams } from "react-router-dom";
import { routes } from "src/data/routes";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "src/components/language-switcher";
import { useAnalytics } from "src/hooks/analytics.hook";
import { usePageTracking } from "src/hooks/tracking-page.hook";
type FieldType = {
  password: string;
  repassword?: string;
};
export default function ResetPassword() {
  const [state, setState] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [errorCode, setErrorCode] = useState<any>();
  const { verifyReset } = useAuth();
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("login");
  const { track } = useAnalytics();
  const { trackPage } = usePageTracking();

  useEffect(() => {
    trackPage();
  }, []);

  useEffect(() => {
    if (state === 1) {
      track("Page", {
        page_current: "password_reset_success",
        page_previous: "password_reset_input",
      });
    }
  }, [state]);

  useEffect(() => {
    track("Error", {
      page_current: "password_reset_input",
      error_message: error,
      error_code: errorCode,
    });
  }, [error]);

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      setLoading(true);
      const { password, repassword } = values;
      if (password !== repassword) {
        setErrorCode("pwdDontMatch");
        throw new Error(t("pwdDontMatch"));
      }
      await verifyReset({ resetToken: resetToken || "", password });
      track("Password reset", {
        page_current: "password_reset_success",
      });
      setState(1);
    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorCode(error?.response?.data?.message || error?.message || "");
        return setError(error?.response?.data?.message || error?.message || "");
      }
      if (error instanceof Error) {
        return setError(error?.message || "");
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = async (values) => {
    track("Error", {
      page_current: "password_reset_input",
      error_message: values?.errorFields[0]?.errors[0],
      error_code: 'passwordTitleErrorMessage',
    });
  };
  return (
    <div className="bg-dark bg-login w-full h-full flex flex-col justify-start items-center">
      <div className="flex justify-end items-center w-full pt-4 pr-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full h-full flex  justify-center items-center">
        <div className="bg-white w-[20rem] md:w-[34rem] p-8 rounded-sm shadow">
          <img src="/logo-white.svg" alt="" className="w-auto h-[40px] mb-8" />
          <h2 className="text-4xl font-semibold">{t("resetPasswordTitle")}</h2>
          {(!state || state === 0) && (
            <h2 className="text-sm mb-4">{t("resetPasswordSubTitle")}</h2>
          )}
          <div className="w-full flex justify-center">
            {(!state || state === 0) && (
              <Form
                name="basic"
                layout="vertical"
                initialValues={{ remember: true }}
                onFinishFailed={onFinishFailed}
                onFinish={onFinish}
                size="large"
                autoComplete="off"
                className="w-full"
              >
                <Form.Item<FieldType>
                  label={
                    <label className="text-lg">
                      {t("resetPasswordField")}
                    </label>
                  }
                  name="password"
                  rules={[
                    { required: true, message: t("passwordTitleErrorMessage") },
                  ]}
                >
                  <Input.Password disabled={loading} />
                </Form.Item>

                <Form.Item<FieldType>
                  label={
                    <label className="text-lg">
                      {t("resetPasswordFieldAgain")}
                    </label>
                  }
                  name="repassword"
                  rules={[
                    { required: true, message: t("passwordTitleErrorMessage") },
                  ]}
                >
                  <Input.Password disabled={loading} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    loading={loading}
                    htmlType="submit"
                    size="large"
                    className="w-full mt-8 bg-dark text-white"
                  >
                    {t("sendBtnTitle")}
                  </Button>
                  <h6 className="text-red-600 mt-1">{error}</h6>
                  <Link
                    to={routes.LOGIN}
                    className="cursor-pointer underline hover:underline   text-dark hover:text-blue-700 mt-1"
                  >
                    {t("signInLinkTitle")}
                  </Link>
                </Form.Item>
              </Form>
            )}
            {state === 1 && (
              <div className="flex items-center flex-col justify-center">
                <img
                  src="/pwd-reseted.svg"
                  alt=""
                  className="w-auto h-[15rem]"
                />
                <h2 className="text-2xl font-semibold text-center">
                  {t("pwdReseted")}
                </h2>
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => navigate(routes.LOGIN)}
                  size="large"
                  className="w-full mt-8 bg-dark text-white"
                >
                  {t("goToTheLoginPage")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
